import type { UltrasoundRegion } from '@/models/dicom';
import { isSpectralDoppler, physicalUnitsLabel } from '@/models/usRegionCodes';
import {
  type SpectralCalibration,
  type SpectralRegionResolution,
} from '@/models/spectralDoppler';

/**
 * Select the spectral Doppler region from the DICOM's ultrasoundRegions array.
 * Returns the reason code so the UI can message specifically (Hz refusal vs missing region).
 *
 * Classification uses raw DICOM codes per PS3.3 §C.8.5.5; helpers live in
 * `@/models/usRegionCodes`. `parsedFlags` on the region is the decoded
 * RegionFlags bitfield (priority / scaling / scrolling) — a different DICOM
 * tag and not used for classification.
 */
export function resolveSpectralRegion(
  regions: UltrasoundRegion[] | undefined | null,
  at?: readonly [number, number]
): SpectralRegionResolution {
  if (!regions || regions.length === 0) {
    return { region: null, reason: 'no-region' };
  }

  const spectrals = regions.filter((r) => isSpectralDoppler(r.regionDataType));

  if (spectrals.length === 0) {
    return { region: null, reason: 'no-region' };
  }

  // A scrolling sweep is split into several spectral regions (GE: one per
  // segment between the erase bars) — use the one under the measured point,
  // else the widest (AP-839).
  const region =
    (at && spectrals.find((r) => regionContains(r, at))) ||
    spectrals.reduce((a, b) => (regionWidth(b) > regionWidth(a) ? b : a));

  if (!hasRequiredCalibrationFields(region)) {
    return { region: null, reason: 'missing-calibration' };
  }

  const yLabel = physicalUnitsLabel(region.physicalUnitsYDirection);
  if (yLabel === 'hertz (seconds^-1)') {
    // Raw Doppler shift — would need the Doppler equation + transducer frequency.
    // Deferred to v1.1.
    return { region: null, reason: 'hz-units' };
  }

  const xLabel = physicalUnitsLabel(region.physicalUnitsXDirection);
  if (xLabel !== 'seconds' || yLabel !== 'cm/sec') {
    return { region: null, reason: 'missing-calibration' };
  }

  return { region, reason: 'ok' };
}

function regionWidth(r: UltrasoundRegion): number {
  return (r.regionLocationMaxX1 ?? 0) - (r.regionLocationMinX0 ?? 0);
}

function regionContains(
  r: UltrasoundRegion,
  [px, py]: readonly [number, number]
): boolean {
  return (
    px >= (r.regionLocationMinX0 ?? Infinity) &&
    px <= (r.regionLocationMaxX1 ?? -Infinity) &&
    py >= (r.regionLocationMinY0 ?? Infinity) &&
    py <= (r.regionLocationMaxY1 ?? -Infinity)
  );
}

// ReferencePixelPhysicalValueX/Y are Type 3 (absent = 0), so not required.
function hasRequiredCalibrationFields(region: UltrasoundRegion): boolean {
  return (
    region.physicalDeltaX !== null &&
    region.physicalDeltaY !== null &&
    region.referencePixelX0 !== null &&
    region.referencePixelY0 !== null &&
    region.regionLocationMinX0 !== null &&
    region.regionLocationMinY0 !== null &&
    region.regionLocationMaxX1 !== null &&
    region.regionLocationMaxY1 !== null
  );
}

/**
 * Build a calibration snapshot from a resolved region. The correction angle
 * (in degrees) is read from the region itself. Frozen onto the annotation at
 * completion time so later DICOM switches can't silently miscalibrate stored
 * measurements.
 */
export function buildCalibrationFromRegion(
  region: UltrasoundRegion,
  useAngleCorrection = true
): SpectralCalibration {
  const bboxMinX = region.regionLocationMinX0 as number;
  const bboxMinY = region.regionLocationMinY0 as number;
  const bboxMaxX = region.regionLocationMaxX1 as number;
  const bboxMaxY = region.regionLocationMaxY1 as number;

  const { refX, refY } = normalizeReferencePixel(
    region.referencePixelX0 as number,
    region.referencePixelY0 as number,
    bboxMinX,
    bboxMinY,
    bboxMaxX,
    bboxMaxY
  );

  return {
    regionUuid: region.uuid,
    physicalDeltaX: region.physicalDeltaX as number,
    physicalDeltaY: region.physicalDeltaY as number,
    referencePixelX0: refX,
    referencePixelY0: refY,
    referencePixelPhysicalValueX: region.referencePixelPhysicalValueX ?? 0,
    referencePixelPhysicalValueY: region.referencePixelPhysicalValueY ?? 0,
    bboxMinX,
    bboxMinY,
    bboxMaxX,
    bboxMaxY,
    dopplerCorrectionAngle: region.dopplerCorrectionAngle ?? 0,
    useAngleCorrection,
  };
}

/**
 * DICOM PS3.3 §C.8.5.5.1.6 is read two ways in the wild: some vendors write
 * ReferencePixelX0/Y0 as image-absolute pixel coords, others as offsets
 * relative to RegionLocationMin{X0,Y0}. If the raw ref pixel falls inside the
 * bbox we trust it as image-absolute; otherwise we translate by bbox origin.
 * Either way the returned coords are image-absolute, so downstream
 * pixelToTime/pixelToVelocity math stays in one coordinate space.
 */
export function normalizeReferencePixel(
  refX: number,
  refY: number,
  bboxMinX: number,
  bboxMinY: number,
  bboxMaxX: number,
  bboxMaxY: number
): { refX: number; refY: number } {
  const inside =
    refX >= bboxMinX &&
    refX <= bboxMaxX &&
    refY >= bboxMinY &&
    refY <= bboxMaxY;
  if (inside) return { refX, refY };
  return { refX: bboxMinX + refX, refY: bboxMinY + refY };
}
